import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listOrders } from "@/lib/api";
import { useCurrentEvent } from "@/lib/event-context";
import { fmtEuro, statusLabel, flowLabel, fmtRelative } from "@/lib/formatters";

const STATUS_TABS = [
  { value: "", label: "Alle" },
  { value: "paid", label: "Betaald" },
  { value: "pending", label: "Open" },
  { value: "invoice_sent", label: "Factuur" },
  { value: "cancelled", label: "Geannuleerd" },
  { value: "refunded", label: "Refunds" },
];

const FLOW_TABS = [
  { value: "", label: "Alle flows" },
  { value: "regular", label: "Regulier" },
  { value: "ambassador", label: "Ambassadeur" },
  { value: "sponsor", label: "Sponsor" },
];

export function OrdersIndex() {
  const { data: event } = useCurrentEvent();
  const [status, setStatus] = useState("");
  const [flow, setFlow] = useState("");
  const [q, setQ] = useState("");

  const { data: orders, isLoading } = useQuery({
    queryKey: ["orders", event?.id, status, flow, q],
    queryFn: () => listOrders({ eventId: event?.id, status: status || undefined, flow: flow || undefined, q: q || undefined }),
    enabled: !!event?.id,
  });

  const counts = useMemo(() => {
    const c: Record<string, number> = { "": orders?.length ?? 0 };
    for (const o of orders ?? []) c[o.status] = (c[o.status] ?? 0) + 1;
    return c;
  }, [orders]);

  return (
    <>
      <div className="topbar-actions" style={{ marginBottom: "1rem" }}>
        <input
          className="search"
          placeholder="🔍 Zoek bestelnr, naam, e-mail, bedrijf..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="search" value={flow} onChange={(e) => setFlow(e.target.value)} style={{ width: "auto" }}>
          {FLOW_TABS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      <div className="tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            className={`tab ${status === t.value ? "active" : ""}`}
            onClick={() => setStatus(t.value)}
          >
            {t.label} {counts[t.value] != null && <span className="badge">{counts[t.value]}</span>}
          </button>
        ))}
      </div>

      <div className="panel">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Bestelnr</th><th>Flow</th><th>Klant</th><th>Items</th><th>Totaal</th><th>Status</th><th>Wanneer</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} className="empty"><span className="spinner" /> Laden…</td></tr>}
              {(orders ?? []).map((o) => (
                <tr key={o.id}>
                  <td><code style={{ color: "var(--c-orange)", fontFamily: "var(--f-mono)" }}>{o.order_no}</code></td>
                  <td><small>{flowLabel(o.flow)}</small></td>
                  <td>
                    {o.buyer_first_name} {o.buyer_last_name}
                    {o.buyer_company && <><br /><small>{o.buyer_company}</small></>}
                    <br /><small>{o.buyer_email}</small>
                  </td>
                  <td>
                    {(o.items ?? []).map((it, i) => (
                      <span key={i}>{it.qty}× {it.ticket_type?.code ?? "—"}{i < o.items.length - 1 ? ", " : ""}</span>
                    ))}
                  </td>
                  <td>{fmtEuro(o.total_cents)}</td>
                  <td><span className={`pill-st ${o.status}`}>{statusLabel(o.status)}</span></td>
                  <td>{fmtRelative(o.created_at)}</td>
                  <td>
                    <div className="row-actions">
                      <Link to={`/orders/${o.id}`} className="btn-mini">Open</Link>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && (orders ?? []).length === 0 && (
                <tr><td colSpan={8} className="empty">Geen bestellingen voor deze filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
